import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Ico } from './icons';
import { API } from '../lib/api';

function StarIcon({ filled }) {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16"
         fill={filled ? 'currentColor' : 'none'}
         stroke="currentColor" strokeWidth="1.3">
      <path d="M8 1.5l2 4.2 4.5.7-3.3 3.2.8 4.5L8 11.9l-4 2.2.8-4.5L1.5 6.4l4.5-.7L8 1.5z"/>
    </svg>
  );
}

const SORT_OPTIONS = [
  ['popular', 'Most duplicated'],
  ['exports', 'Most exported'],
  ['stars', 'Most starred'],
  ['recent', 'Most recent'],
  ['name', 'Name A–Z'],
];

export function SmartSearch({
  query, onQueryChange,
  tags, onTagsChange,
  starred, onStarredToggle,
  sort, onSortChange,
  isLoggedIn,
}) {
  const [tagOpen, setTagOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [hoverIdx, setHoverIdx] = useState(0);
  const tagChipRef = useRef(null);
  const tagPopRef = useRef(null);
  const sortPopRef = useRef(null);
  const searchInputRef = useRef(null);
  const tagInputRef = useRef(null);

  useEffect(() => {
    fetch(API.tagsPath, { headers: { 'Accept': 'application/json' } })
      .then(r => {
        if (!r.ok) {
          const e = new Error(`HTTP ${r.status} loading tags`);
          e.status = r.status;
          throw e;
        }
        return r.json();
      })
      .then(data => setAllTags(data.tags || []))
      .catch(err => {
        console.error('tag fetch failed:', err);
        setAllTags([]);
      });
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (tagOpen && tagPopRef.current && !tagPopRef.current.contains(e.target) &&
          !tagChipRef.current?.contains(e.target)) {
        setTagOpen(false);
      }
      if (sortOpen && sortPopRef.current && !sortPopRef.current.contains(e.target) &&
          !e.target.closest('.ss-sort-btn')) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [tagOpen, sortOpen]);

  const selectedSet = useMemo(() => new Set(tags), [tags]);
  const needle = tagInput.trim().toUpperCase();

  const candidates = useMemo(() => {
    const filtered = allTags.filter(t => !needle || t.tag.includes(needle));
    return filtered.sort((a, b) => {
      const aSel = selectedSet.has(a.tag) ? 0 : 1;
      const bSel = selectedSet.has(b.tag) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      if (needle) {
        const aPref = a.tag.startsWith(needle) ? 0 : 1;
        const bPref = b.tag.startsWith(needle) ? 0 : 1;
        if (aPref !== bPref) return aPref - bPref;
      }
      return (b.count || 0) - (a.count || 0);
    }).slice(0, 50);
  }, [allTags, needle, selectedSet]);

  const toggleTag = (tag) => {
    const upper = tag.trim().toUpperCase();
    if (!upper) return;
    if (selectedSet.has(upper)) {
      onTagsChange(tags.filter(t => t !== upper));
    } else {
      onTagsChange([...tags, upper]);
    }
    setTagInput('');
    setHoverIdx(0);
    tagInputRef.current?.focus();
  };

  const onTagKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIdx(i => Math.min(i + 1, Math.max(candidates.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = candidates[hoverIdx]?.tag || tagInput;
      if (pick) toggleTag(pick);
    } else if (e.key === 'Escape') {
      setTagOpen(false);
    }
  };

  const handleTagChipClick = () => {
    setSortOpen(false);
    setTagOpen(o => !o);
    setTimeout(() => tagInputRef.current?.focus(), 0);
  };

  const handleSortClick = () => {
    setTagOpen(false);
    setSortOpen(o => !o);
  };

  const sortLbl = SORT_OPTIONS.find(([v]) => v === sort)?.[1] || 'Most exported';

  return (
    <div className="ss">
      {/* Toolbar: search bar + tag chip + starred + sort */}
      <div className="ss-toolbar">
        <div className="docs-search ss-search">
          <Ico.Search width="14" height="14"/>
          {tags.length > 0 && (
            <div className="ss-search-tags">
              {tags.map(tag => (
                <span key={tag} className="chip chip--tag ss-search-tag">
                  {tag}
                  <button type="button" className="chip__remove"
                          onClick={() => onTagsChange(tags.filter(t => t !== tag))}
                          aria-label={`Remove ${tag}`}>×</button>
                </span>
              ))}
            </div>
          )}
          <input ref={searchInputRef}
                 type="text"
                 placeholder="Search by name, description, tags, or author..."
                 value={query}
                 onChange={e => onQueryChange(e.target.value)}/>
          {(query || tags.length > 0) && (
            <button className="ds-clear"
                    onClick={() => { onQueryChange(''); if (tags.length > 0) onTagsChange([]); }}
                    aria-label="Clear search">×</button>
          )}
        </div>

        <div className="ss-filter-wrap" ref={tagChipRef}>
          <button type="button"
                  className={`btn btn--ghost btn--lg ${tags.length > 0 ? 'cm-starred-filter--on' : ''}`}
                  onClick={handleTagChipClick}
                  aria-haspopup="listbox"
                  aria-expanded={tagOpen}>
            {tags.length > 0 ? `Filter (${tags.length})` : 'Filter'}
          </button>

          {tagOpen && (
            <div className="ss-popover ss-popover--tags" ref={tagPopRef}>
              <div className="ss-pop-search">
                <Ico.Search width="12" height="12"/>
                <input ref={tagInputRef}
                       type="text"
                       role="combobox"
                       aria-controls="ss-tag-listbox"
                       aria-expanded={tagOpen}
                       aria-autocomplete="list"
                       aria-activedescendant={candidates[hoverIdx] ? `ss-tag-opt-${hoverIdx}` : undefined}
                       className="ss-pop-input"
                       placeholder="Search..."
                       value={tagInput}
                       onChange={e => { setTagInput(e.target.value); setHoverIdx(0); }}
                       onKeyDown={onTagKeyDown}/>
                {tagInput && (
                  <button type="button" className="ss-clear" onClick={() => setTagInput('')} aria-label="Clear tag search">×</button>
                )}
              </div>
              <div id="ss-tag-listbox" className="ss-pop-list" role="listbox" aria-multiselectable="true">
                {candidates.length > 0 ? candidates.map((t, i) => {
                  const isSelected = selectedSet.has(t.tag);
                  return (
                    <button key={t.tag}
                            id={`ss-tag-opt-${i}`}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`ss-pop-option ${i === hoverIdx ? 'ss-pop-option--hover' : ''} ${isSelected ? 'ss-pop-option--selected' : ''}`}
                            onMouseEnter={() => setHoverIdx(i)}
                            onMouseDown={(e) => { e.preventDefault(); toggleTag(t.tag); }}>
                      <span className="ss-pop-check" aria-hidden="true">
                        {isSelected ? '✓' : ''}
                      </span>
                      <span className="ss-pop-label">{t.tag}</span>
                      {t.count > 0 && <span className="ss-pop-count">{t.count}</span>}
                    </button>
                  );
                }) : needle ? (
                  <button type="button" className="ss-pop-option"
                          onMouseDown={(e) => { e.preventDefault(); toggleTag(tagInput); }}>
                    <span className="ss-pop-check" aria-hidden="true">+</span>
                    <span className="ss-pop-label">Add &ldquo;{needle}&rdquo;</span>
                  </button>
                ) : (
                  <div className="ss-pop-empty ty-body-sm">No tags available</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lib-toolbar-right">
          {isLoggedIn && (
            <button type="button"
                    className={`btn btn--ghost btn--lg ${starred ? 'cm-starred-filter--on' : ''}`}
                    onClick={onStarredToggle}
                    aria-pressed={starred}>
              <StarIcon filled={starred}/>
              Starred
            </button>
          )}
          <div className="cm-sort-wrap">
            <button className="btn btn--ghost btn--lg ss-sort-btn"
                    onClick={handleSortClick}
                    onKeyDown={e => { if (sortOpen && e.key === 'Escape') { e.preventDefault(); setSortOpen(false); } }}
                    aria-haspopup="menu"
                    aria-expanded={sortOpen}>
              Sort: {sortLbl}
              <Ico.ChevronDown width="12" height="12"/>
            </button>
            {sortOpen && (
              <>
                <div className="dc-menu-scrim" onClick={() => setSortOpen(false)}/>
                <div className="menu cm-sort-menu" ref={sortPopRef} role="menu">
                  {SORT_OPTIONS.map(([val, label]) => (
                    <button key={val} role="menuitem"
                            className={`menu__item${sort === val ? ' menu__item--active' : ''}`}
                            onClick={() => { onSortChange(val); setSortOpen(false); }}
                            onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setSortOpen(false); } }}>
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
